import { NEW_PATH_DELIMITER } from '@picsio/db/src/constants';
import localization from '../../../shared/strings';
import * as utils from '../../../shared/utils';

/** Mirrors server list default & SavedSearchesTree UI default. */
export const DEFAULT_SAVED_SEARCH_TREE_SORT = {
  type: 'createdAt',
  order: 'asc',
};

/**
 * Same comparison intent as server `sortCollections` / `sortCollectionsChildren`.
 */
function compareSavedSearchTreeNodes(a, b, sortType) {
  const type = sortType?.type || DEFAULT_SAVED_SEARCH_TREE_SORT.type;
  const order = sortType?.order || DEFAULT_SAVED_SEARCH_TREE_SORT.order;
  const baseSort = { type: 'name', order: 'asc' };

  if (type === 'custom') {
    const orderA = typeof a.order === 'number' ? a.order : 99999;
    const orderB = typeof b.order === 'number' ? b.order : 99999;
    if (orderA !== orderB) return orderA - orderB;
  }

  const A = type === 'custom' ? a[baseSort.type] : a[type];
  const B = type === 'custom' ? b[baseSort.type] : b[type];

  let x = typeof A === 'string' ? A.toLowerCase() : A;
  let y = typeof B === 'string' ? B.toLowerCase() : B;

  if (type === 'createdAt') {
    x = A ? new Date(A).getTime() : 0;
    y = B ? new Date(B).getTime() : 0;
  }

  const baseOrder = type === 'custom' ? baseSort.order : order;

  if (x < y) return baseOrder === 'asc' ? -1 : 1;
  if (x > y) return baseOrder === 'asc' ? 1 : -1;
  return 0;
}

function sortSavedSearchLevel(nodes, sortType) {
  if (!Array.isArray(nodes) || !nodes.length) return;
  if (nodes.length > 1) {
    // Same as collections: freshly created / pending rows stay pinned to the top
    // of their sibling list; the rest follow the user’s sort preference.
    nodes.sort((a, b) => {
      const ap = Boolean(a.__pinTop);
      const bp = Boolean(b.__pinTop);
      if (ap !== bp) return ap ? -1 : 1;
      return compareSavedSearchTreeNodes(a, b, sortType);
    });
  }
  nodes.forEach((n) => {
    if (n && n.isGroup && n.nodes?.length) sortSavedSearchLevel(n.nodes, sortType);
  });
}

/**
 * Make SavedSearches tree from list
 *
 * Two kinds of documents are produced by the API:
 *  - real saved searches (leaves): rendered inside their virtual group node
 *  - "ghost" group placeholders (`isGroupPlaceholder: true`): never rendered
 *    as leaves; they exist solely so an empty group node persists across
 *    reloads.
 *
 * @param {Object[]} items
 * @param {string} userId
 * @param {string?} searchQuery
 * @returns {Object}
 */
export default function makeTree(
  items,
  userId,
  searchQuery = '',
  sortType = DEFAULT_SAVED_SEARCH_TREE_SORT,
) {
  const list = items.map((svdSearch) => ({ ...svdSearch }));
  const tree = {
    favorites: {
      name: localization.SAVEDSEARCHESTREE.textFavorites,
      path: 'favorites',
      root: true,
      isOpen:
				utils.getCookie('picsio.savedSearchedTree.favorites.open') === null
				|| utils.getCookie('picsio.savedSearchedTree.favorites.open'),
      nodes: [],
    },
    teamSearches: {
      name: localization.SAVEDSEARCHESTREE.textTeamSearches,
      path: 'teamSearches',
      root: true,
      isOpen:
				utils.getCookie('picsio.savedSearchedTree.teamSearches.open') === null
				|| utils.getCookie('picsio.savedSearchedTree.teamSearches.open'),
      nodes: [],
    },
    personalSearches: {
      name: localization.SAVEDSEARCHESTREE.textSavedSearches,
      path: 'savedSearches',
      root: true,
      isOpen:
				utils.getCookie('picsio.savedSearchedTree.savedSearches.open') === null
				|| utils.getCookie('picsio.savedSearchedTree.savedSearches.open'),
      nodes: [],
    },
  };

  // Process real searches first so virtual groups they imply are created;
  // placeholders are then layered on top to (a) materialize the empty groups
  // they represent and (b) attach a stable `_id` to the corresponding virtual
  // group node (useful for delete-empty-group / drag-and-drop later).
  const realSearches = [];
  const placeholders = [];
  list.forEach((s) => {
    if (s?.isGroupPlaceholder) placeholders.push(s);
    else realSearches.push(s);
  });

  /**
   * Push or unshift a node based on its `__pinTop` flag (matches collections
   * tree behavior for inline-created folders).
   */
  const insert = (nodes, node) => {
    if (node.__pinTop) nodes.unshift(node);
    else nodes.push(node);
  };

  /**
   * Recursively walk a path and either insert the leaf or materialize the
   * group node at the end of the path.
   */
  const walk = (svdSearch, isPlaceholder, path, nodes) => {
    const name = path.shift();
    const found = nodes.find((node) => node.name === name && node.isGroup);
    if (found) {
      if (path.length === 0) {
        if (isPlaceholder) {
          // Group already exists from descendants. Tag it with the
          // placeholder so the UI can later delete the placeholder doc when
          // the group is emptied. Carry through pending/pinned flags so the
          // existing virtual group reflects the in-flight state.
          found.placeholderId = svdSearch._id;
          // Virtual rows used path as _id; API routes need Mongo ids (move, etc.).
          found._id = svdSearch._id;
          // Keep scope aligned with the placeholder row (e.g. optimistic
          // create uses the real team id) so resolveSavedSearchTreeNodeDocumentId
          // matches on teamId.
          if (svdSearch.teamId != null) found.teamId = svdSearch.teamId;
          if (svdSearch.isBusy) found.isBusy = true;
          if (svdSearch.__pinTop) found.__pinTop = true;
          if (svdSearch.createdAt) found.createdAt = svdSearch.createdAt;
        } else {
          found.nodes.push(svdSearch);
        }
        return;
      }
      walk(svdSearch, isPlaceholder, path, found.nodes);
      return;
    }
    if (path.length === 0) {
      if (isPlaceholder) {
        insert(nodes, {
          _id: svdSearch._id,
          name,
          path: svdSearch.path,
          isGroup: true,
          teamId: svdSearch.teamId,
          placeholderId: svdSearch._id,
          isBusy: Boolean(svdSearch.isBusy),
          __pinTop: Boolean(svdSearch.__pinTop),
          createdAt: svdSearch.createdAt,
          nodes: [],
        });
      } else {
        nodes.push(svdSearch);
      }
      return;
    }
    // Need to materialize a virtual group on the way down.
    const remaining = path.length;
    const fullPath = svdSearch.path.split(NEW_PATH_DELIMITER);
    const groupPath = fullPath.slice(0, fullPath.length - remaining).join(NEW_PATH_DELIMITER);
    const newNode = {
      _id: groupPath,
      name,
      path: groupPath,
      isGroup: true,
      readonly: svdSearch.readonly,
      dynamic: svdSearch.dynamic,
      teamId: svdSearch.teamId,
      createdAt: svdSearch.createdAt,
      nodes: [],
    };
    nodes.push(newNode);
    walk(svdSearch, isPlaceholder, path, newNode.nodes);
  };

  const place = (svdSearch, isPlaceholder) => {
    const pathArr = svdSearch?.path?.split(NEW_PATH_DELIMITER);

    if (!pathArr) {
      // No path: ungrouped leaf. Placeholders without a path are nonsensical
      // (they'd describe "an empty root scope") and are dropped defensively.
      if (isPlaceholder) return;
      if (searchQuery && !svdSearch.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return;
      }
      if (svdSearch.teamId) tree.teamSearches.nodes.push(svdSearch);
      else tree.personalSearches.nodes.push(svdSearch);
      return;
    }

    pathArr.shift(); // drop the leading empty string from the delimiter prefix

    if (searchQuery) {
      // Search-mode flattens the tree by name match. Placeholders carry the
      // group name as their `name`; matching them would produce a confusing
      // root-level entry, so they are filtered out of search results.
      if (isPlaceholder) return;
      if (svdSearch.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        if (svdSearch.teamId) tree.teamSearches.nodes.push(svdSearch);
        else tree.personalSearches.nodes.push(svdSearch);
      }
      return;
    }

    if (svdSearch.teamId) walk(svdSearch, isPlaceholder, pathArr, tree.teamSearches.nodes);
    else walk(svdSearch, isPlaceholder, pathArr, tree.personalSearches.nodes);
  };

  realSearches.forEach((s) => place(s, false));
  placeholders.forEach((s) => place(s, true));

  tree.favorites.nodes = list.filter(
    (node) => {
      if (node.isGroupPlaceholder) return false;
      if (searchQuery && !node.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return node.favorites?.length && node.favorites.includes(userId);
    },
  ).map((node) => ({
    ...node,
    treeNodeId: node.isGroup && node.path != null ? node.path : node._id,
  }));

  /**
   * Annotate every group with a `hasChild` flag mirroring CollectionsTree.
   * The flag is what allows render-side lazy expansion: a closed group can
   * have its `nodes` trimmed away (so React does not build elements for the
   * subtree) while the toggle caret remains visible because `hasChild` is
   * truthy.
   *
   * `treeNodeId` is the stable expansion/selection key for @picsio/ui Tree:
   * groups use `path` (Mongo `_id` can replace virtual `_id` when a placeholder
   * merges) and leaves use `_id`.
   */
  const annotate = (nodes) => {
    if (!Array.isArray(nodes)) return;
    nodes.forEach((n) => {
      if (!n) return;
      n.treeNodeId = n.isGroup && n.path != null ? n.path : n._id;
      if (n.isGroup) {
        n.hasChild = (n.nodes || []).length > 0;
        annotate(n.nodes);
      }
    });
  };
  annotate(tree.personalSearches.nodes);
  annotate(tree.teamSearches.nodes);

  sortSavedSearchLevel(tree.teamSearches.nodes, sortType);
  sortSavedSearchLevel(tree.personalSearches.nodes, sortType);

  return tree;
}
